import { MatDialogRef } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { SafeUrl } from '@angular/platform-browser';
import { createAction, props } from '@ngrx/store';
import {
  CustomerServiceNote,
  CustomerServiceNoteDetail,
  DeactivationReason,
  Driver,
  DriverAnnualRideExecutionData,
  DriverBank,
  DriverBankTransaction,
  DriverBankTransactionReason,
  DriverSuspensionDetail,
  Person,
  Trip
} from '@uzurv/clara';
import { HttpException } from '@uzurv/core';
import {
  Address,
  Driver as DOTSDriver,
  MvrDetails,
  RandomScreeningDriverSelection,
  RandomScreeningDriverSelectionInfo,
  RandomScreeningYearlyProgress,
  RefusalReason,
  RuleValue,
  Screening,
  SubstanceScreening,
  TaxDocument,
  Vehicle
} from '@uzurv/dots';

import { UzurvFilterDefinition } from '@uzurv/http';
import { DriverBankTransactionData } from '../../components/driver-bank';
import { DriverState } from './driver.reducer';
import { VerifiedAddress } from 'projects/sdk/packages/dots/src/models/verified-address';
import { Tab } from '../../services/tab/tab.service';

export const GetDriverPayStatements = createAction('[Driver] Get driver pay statements', props<{ startDate: Date; endDate: Date }>());

export const GetDriverPayStatementsSuccess = createAction('[Driver] Get driver pay statements success');

export const GetDriverPayStatementsFail = createAction('[Driver] Get driver pay statements fail', props<{ error: any }>());

export const OpenEditDriverProfileDialog = createAction(
  '[Driver] Open edit driver profile dialog',
  props<{ driver?: Driver; address?: Address }>()
);

export const EditDriverProfile = createAction(
  '[Driver] Edit driver name',
  props<{ driver: Driver; address?: Address; displayAddressValidationDialog?: boolean }>()
);

export const EditDriverProfileSuccess = createAction(
  '[Driver] Update driver profile success',
  props<{ driver: Driver; address?: Address; displayAddressValidationDialog: boolean; verifiedAddress?: VerifiedAddress }>()
);

export const EditDriverProfileFail = createAction('[Driver] Update driver profile fail', props<{ error: HttpException }>());

export const Start = createAction('[Driver] Start', props<Tab<DriverState>>());

export const RestoreState = createAction('[Driver] Restore state', props<{ state: DriverState }>());

export const DriverSearch = createAction('[Driver] Search', props<{ searchString: string }>());

export const DriverSearchSuccess = createAction('[Driver] Search success', props<{ drivers: Array<Driver> }>());

export const DriverSearchFail = createAction('[Driver] Search failed', props<{ error: HttpException }>());

export const DriverSelected = createAction('[Driver] Select driver', props<{ driver: Driver }>());

export const ClearDriverSearch = createAction('[Driver] Clear search');

export const ShowMoreDriverSearchResults = createAction('[Driver] show more search result');

export const GetDriver = createAction('[Driver] Get driver', props<{ driverId: number }>());

export const GetDriverSuccess = createAction('[Driver] Get driver success', props<{ driver: Driver }>());

export const GetDriverFail = createAction('[Driver] Get driver fail', props<{ error: HttpException }>());

export const GetVehicles = createAction('[Driver] Get vehicles');

export const GetVehiclesSuccess = createAction('[Driver] Get vehicles success', props<{ vehicles: Array<Vehicle> }>());

export const GetVehiclesFail = createAction('[Driver] Get vehicles fail', props<{ error: HttpException }>());

export const GetDriverProfilePicture = createAction('[Driver] Get driver profile picture url');

export const GetDriverProfilePictureSuccess = createAction('[Driver] Get driver profile picture success', props<{ safeUrl: SafeUrl }>());

export const GetDriverProfilePictureFail = createAction('[Driver] Get driver  profile picture fail', props<{ error: HttpException }>());

export const GetDriverBank = createAction('[Driver] Get driver bank');

export const GetDriverBankSuccess = createAction('[Driver] Get driver bank sucess', props<{ driverBank: DriverBank }>());

export const GetDriverBankFail = createAction('[Driver] Get driver bank fail', props<{ error: HttpException }>());

export const GetDriverBankTransactionCreditReasons = createAction('[Driver] Get driver bank transaction credit reasons');

export const GetDriverBankTransactionCreditReasonsSuccess = createAction(
  '[Driver] Get driver bank transaction credit reasons success',
  props<{ driverBankTransactionCreditReasons: Array<DriverBankTransactionReason> }>()
);

export const GetDriverBankTransactionCreditReasonsFail = createAction(
  '[Driver] Get driver bank transaction credit reasons fail',
  props<{ error: HttpException }>()
);

export const GetDriverBankTransactionDebitReasons = createAction('[Driver] Get driver bank transaction debit reasons');

export const GetDriverBankTransactionDebitReasonsSuccess = createAction(
  '[Driver] Get driver bank transaction debit reasons success',
  props<{ driverBankTransactionDebitReasons: Array<DriverBankTransactionReason> }>()
);

export const GetDriverBankTransactionDebitReasonsFail = createAction(
  '[Driver] Get driver bank transaction debit reasons fail',
  props<{ error: HttpException }>()
);

export const GetCustomerServiceNotes = createAction('[Driver] Get customer service notes');

export const GetCustomerServiceNotesSuccess = createAction(
  '[Driver] Get customer service notes success',
  props<{ notes: Array<CustomerServiceNoteDetail> }>()
);

export const GetCustomerServiceNotesFail = createAction('[Driver] Get customer service notes fail', props<{ error: HttpException }>());

export const AddCustomerServiceNotesPage = createAction('[Driver] Customer service notes pagination');

export const CreateCustomerServiceNoteForDriver = createAction(
  '[Driver] Create customer service note for driver',
  props<{ customerServiceNote: CustomerServiceNote }>()
);

export const CreateCustomerServiceNoteForDriverSuccess = createAction('[Driver] Create customer service note for driver success');

export const CreateCustomerServiceNoteForDriverFail = createAction(
  '[Driver] Create customer service note for driver fail',
  props<{ error: HttpException }>()
);

export const GetDriverBankTransactions = createAction('[Driver] Get driver bank transactions');

export const GetDriverBankTransactionsSuccess = createAction(
  '[Driver] Get driver bank transactions success',
  props<{ bankTransactions: Array<DriverBankTransaction> }>()
);

export const GetDriverBankTransactionsFail = createAction('[Driver] Get driver bank transactions fail', props<{ error: HttpException }>());

export const DriverBankTransactionsPagination = createAction(
  '[Driver] Driver bank transactions pagination',
  props<{ pageEvent: PageEvent }>()
);

export const GetDriverScreenings = createAction('[Driver] Get driver screenings');

export const GetDriverScreeningsSuccess = createAction('[Driver] Get driver screenings success', props<{ screenings: Array<Screening> }>());

export const GetDriverScreeningsFail = createAction('[Driver] Get driver screenings fail', props<{ error: HttpException }>());

export const DriverScreeningsPagination = createAction('[Driver] Driver screenings pagination', props<{ pageEvent: PageEvent }>());

export const ConfirmCancelScreening = createAction('[Driver] Confirm cancel screening', props<{ screeningId: number }>());

export const CancelScreening = createAction(
  '[Driver] Cancel screening',
  props<{ payload: { dotsDriverId: number; screeningId: number } }>()
);

export const CancelScreeningSuccess = createAction('[Driver] Cancel Screening Success');

export const CancelScreeningFail = createAction('[Driver] Cancel Screening fail', props<{ error: HttpException }>());

export const CancelRandomScreening = createAction(
  '[Driver] Cancel random screening',
  props<{ randomScreeningDriverSelection: RandomScreeningDriverSelection }>()
);

export const CancelRandomScreeningSuccess = createAction('[Driver] Cancel Random Screening Success');

export const CancelRandomScreeningFail = createAction('[Driver] Cancel Random Screening fail', props<{ error: HttpException }>());

export const ConfirmChangeFacility = createAction('[Driver] Confirm change facility', props<{ dotsDriverId; screeningId: number }>());

export const ChangeFacility = createAction('[Driver] Change facility', props<{ dotsDriverId: number; screeningId: number }>());

export const ChangeFacilitySuccess = createAction('[Driver] Change facility success');

export const ChangeFacilityFail = createAction('[Driver] Change facility fail', props<{ error: HttpException }>());

export const GetDriverReservations = createAction('[Driver] Get driver reservations');

export const GetDriverReservationsSuccess = createAction(
  '[Driver] Get driver reservations success',
  props<{ reservations: Array<Trip> }>()
);

export const GetDriverReservationsFail = createAction('[Driver] Get driver reservations fail', props<{ error: HttpException }>());

export const SelectGetTransactionReason = createAction(
  '[Driver] Select get transaction reason',
  props<{ bankTransactionData: DriverBankTransactionData }>()
);

export const SelectGetTransactionReasonSuccess = createAction(
  '[Driver] Select get transaction reason success',
  props<{ payload: { transactionReason: DriverBankTransactionReason; bankTransactionData: DriverBankTransactionData } }>()
);

export const CreateDriverBankTransactionForDriver = createAction(
  '[Driver] Create driver bank transaction for driver',
  props<{ payload: { driverBankTransaction: DriverBankTransaction; reverseTransaction: boolean } }>()
);

export const CreateDriverBankTransactionForDriverSuccess = createAction(
  '[Driver] Create driver bank transaction for driver success',
  props<{ payload: { driverBankTransaction: DriverBankTransaction; reverseTransaction: boolean } }>()
);

export const CreateDriverBankTransactionForDriverFail = createAction(
  '[Driver] Create driver bank transaction for driver fail',
  props<{ error: HttpException }>()
);

export const AbandonSubmission = createAction('[Driver] Abandon submission');

export const CreateTransactionNoteSuccess = createAction('[Driver] Create transaction note success');

export const CreateTransactionNoteFail = createAction('[Driver] Create transaction note fail', props<{ error: HttpException }>());

export const ConfirmReverseTransaction = createAction(
  '[Driver] Confirm reverse transaction',
  props<{ driverBankTransaction: DriverBankTransaction }>()
);

export const DriverBankTransactionFiltersChanged = createAction(
  '[Driver] Driver bank transaction filters changed',
  props<{ filters: Array<UzurvFilterDefinition> }>()
);

export const ReservationsPagination = createAction('[Driver] Reservations Pagination', props<{ pageEvent: PageEvent }>());

export const GetDriverAddress = createAction('[Driver] Get driver address', props<{ dotIdentifier: number }>());

export const GetDriverAddressSuccess = createAction('[Driver] Get driver address success', props<{ address: Address }>());

export const GetDriverAddressFail = createAction('[Driver] Get driver address fail', props<{ error: HttpException }>());

export const GoToDriverAddress = createAction('[Driver] Go to driver address');

export const GoToDocuments = createAction('[Driver] Go to documents');

export const GetDriverSuspensions = createAction('[Driver] Get driver suspensions', props<{ driverId: number }>());

export const GetDriverSuspensionsSuccess = createAction(
  '[Driver] Get driver suspensions success',
  props<{ driverSuspensions: Array<DriverSuspensionDetail> }>()
);

export const GetDriverSuspensionsFail = createAction('[Driver] Get driver suspensions fail', props<{ error: HttpException }>());

export const GetDeactivationReasons = createAction('[Driver] Get deactivation reasons');

export const GetDeactivationReasonsSuccess = createAction(
  '[Driver] Get deactivation reasons success',
  props<{ deactivationReasons: Array<DeactivationReason> }>()
);

export const GetDeactivationReasonsFail = createAction('[Driver] Get deactivation reasons fail', props<{ error: HttpException }>());

export const GoToDriverSuspensions = createAction('[Driver] Go to driver suspensions');

export const ViewReferrer = createAction('[Driver] View referrer');

export const ViewReferrerSuccess = createAction('[Driver] View referrer success', props<{ referrer: Person }>());

export const ViewReferrerFail = createAction('[Driver] View referrer fail', props<{ error: HttpException }>());

export const EditReferrer = createAction('[Driver] Edit referrer');

export const SearchForReferrer = createAction('[Driver] Search for referrer', props<{ searchString: string }>());

export const SearchForReferrerSuccess = createAction(
  '[Driver] Search for referrer success',
  props<{ referrerSearchResults: Array<Person> }>()
);

export const SearchForReferrerFail = createAction('[Driver] Search for referrer fail', props<{ error: HttpException }>());

export const SelectReferrer = createAction('[Driver] Select referrer', props<{ selectedReferrer: Person }>());

export const SetReferrer = createAction('[Driver] Set referrer');

export const SetReferrerSuccess = createAction('[Driver] Set referrer success', props<{ referrer: Person }>());

export const SetReferrerFail = createAction('[Driver] Set referrer fail', props<{ error: HttpException }>());

export const ClearReferrerSearch = createAction('[Driver] Clear referrer search');

export const ShowMoreReferrers = createAction('[Driver] Show more referrers', props<{ searchString: string }>());

export const CancelEditReferrer = createAction('[Driver] Cancel edit referrer');

export const ExpandScreening = createAction('[Driver] Expand screening', props<{ screening: Screening }>());

export const GetSubstanceScreening = createAction('[Driver] Get substance screening', props<{ screening: Screening }>());

export const GetSubstanceScreeningSuccess = createAction(
  '[Driver] Get substance screening success',
  props<{ substanceScreening: SubstanceScreening }>()
);

export const GetSubstanceScreeningFail = createAction('[Driver] Get substance screening fail', props<{ error: HttpException }>());

export const ClearSubstanceScreening = createAction('[Driver] Clear substance screening');

export const GetMVRScreeningDetails = createAction('[Driver] Get MVR screening details', props<{ screening: Screening }>());

export const GetMVRScreeningDetailsSuccess = createAction(
  '[Driver] Get MVR screening details success',
  props<{ mvrOrderDetails: MvrDetails }>()
);

export const GetMVRScreeningDetailsFail = createAction('[Driver] Get MVR screening details fail', props<{ error: HttpException }>());

export const CreateSubstanceScreening = createAction(
  '[Driver] Create substance screening',
  props<{
    dotsDriverId: number;
    screeningPackageCode: string;
    screeningReasonCode: string;
    isRandom: boolean;
    dialog?: MatDialogRef<any>;
  }>()
);

export const CreateSubstanceScreeningSuccess = createAction('[Driver] Create substance screening success');

export const CreateSubstanceScreeningFail = createAction('[Driver] Create substance screening fail', props<{ error: HttpException }>());

export const CreateRandomSubstanceScreening = createAction(
  '[Driver] Create random substance screening',
  props<{
    dotsDriverId: number;
    screeningPackageCode: string;
    dialog?: MatDialogRef<any>;
  }>()
);

export const CreateRandomSubstanceScreeningSuccess = createAction('[Driver] Create substance screening success');

export const CreateRandomSubstanceScreeningFail = createAction(
  '[Driver] Create substance screening fail',
  props<{ error: HttpException }>()
);

export const GetMVRReport = createAction('[Driver] Get MVR report', props<{ screeningId: number }>());

export const GetMVRReportSuccess = createAction('[Driver] Get MVR report success');

export const GetMVRReportFail = createAction('[Driver] Get MVR report fail', props<{ error: HttpException }>());

export const ResendScreeningRequest = createAction(
  '[Driver] Resend screening request',
  props<{ screeningPackageCode: string; screeningReasonCode: string }>()
);

export const ResendScreeningRequestSuccess = createAction('[Driver] Resend screening request success', props<{ screening: Screening }>());

export const ResendScreeningRequestFail = createAction('[Driver] Resend screening request fail', props<{ error: HttpException }>());

export const GetRandomScreeningYearlyProgress = createAction('[Driver] Get random screening yearly progress');

export const GetRandomScreeningYearlyProgressSuccess = createAction(
  '[Driver] Get random screening yearly progress success',
  props<{ randomScreeningYearlyProgress: RandomScreeningYearlyProgress }>()
);

export const GetRandomScreeningYearlyProgressFail = createAction(
  '[Driver] Get random screening yearly progress fail',
  props<{ error: HttpException }>()
);

export const GetRandomScreeningDriverSelectionInfos = createAction('[Driver] Get random screening driver selection infos');

export const GetRandomScreeningDriverSelectionInfosSuccess = createAction(
  '[Driver] Get random screening driver selection infos success',
  props<{ randomScreeningDriverSelectionInfos: Array<RandomScreeningDriverSelectionInfo> }>()
);

export const GetRandomScreeningDriverSelectionInfosFail = createAction(
  '[Driver] Get random screening driver selection infos fail',
  props<{ error: HttpException }>()
);

export const GetRandomScreeningDriverSelectionInfosPagination = createAction(
  '[Driver] Get random screening driver selection infos pagination',
  props<{ pageEvent: PageEvent }>()
);

export const GetRandomScreeningDriverSelection = createAction(
  '[Driver] Get random screening driver selection',
  props<{ randomScreeningDriverSelectionInfo: RandomScreeningDriverSelectionInfo }>()
);

export const GetRandomScreeningDriverSelectionSuccess = createAction(
  '[Driver] Get random screening driver selection success',
  props<{ randomScreeningDriverSelection: RandomScreeningDriverSelection }>()
);

export const GetRandomScreeningDriverSelectionFail = createAction(
  '[Driver] Get random screening driver selection fail',
  props<{ error: HttpException }>()
);

export const GetRefusalReasons = createAction('[Driver] Get refusal reasons');

export const GetRefusalReasonsSuccess = createAction(
  '[Driver] Get refusal reasons success',
  props<{ refusalReasons: Array<RefusalReason> }>()
);

export const GetRefusalReasonsFail = createAction('[Driver] Get refusal reasons fail', props<{ error: HttpException }>());

export const GetRandomScreeningConfigurationValues = createAction('[Driver] Get random screening configuration values');

export const GetRandomScreeningConfigurationValuesSuccess = createAction(
  '[Driver] Get random screening configuration values success',
  props<{ randomScreeningConfigurationValues: Array<RuleValue> }>()
);

export const GetRandomScreeningConfigurationValuesFail = createAction(
  '[Driver] Get random screening configuration values fail',
  props<{ error: HttpException }>()
);

export const EditRandomScreeningConfigurationValue = createAction(
  '[Driver] Edit random screening configuration value',
  props<{ ruleValueName: string }>()
);

export const CancelEditRandomScreeningConfigurationValue = createAction('[Driver] Cancel edit random screening configuration value');

export const ConfirmUpdateRandomScreeningConfigurationValue = createAction(
  '[Driver] Confirm update random screening configuration value',
  props<{ updatedValue: string }>()
);

export const UpdateRandomScreeningConfigurationValue = createAction('[Driver] Update random screening configuration value');

export const UpdateRandomScreeningConfigurationValueSuccess = createAction(
  '[Driver] Update random screening configuration value success',
  props<{ ruleValue: RuleValue }>()
);

export const UpdateRandomScreeningConfigurationValueFail = createAction(
  '[Driver] Update random screening configuration value fail',
  props<{ error: HttpException }>()
);

export const ConfirmCreateNewRandomScreening = createAction(
  '[Driver] Confirm create new random screening',
  props<{ dotsDriverId: number }>()
);

export const GoToDriver = createAction('[Driver] Go to driver', props<{ dotsDriver: DOTSDriver }>());

export const GetDriverAnnualRideExecutionData = createAction('[Driver] Get driver annual ride execution data');

export const GetDriverAnnualRideExecutionDataSuccess = createAction(
  '[Driver] Get driver annual ride execution data success',
  props<{ driverAnnualRideExecutionData: Array<DriverAnnualRideExecutionData> }>()
);

export const GetDriverAnnualRideExecutionDataFail = createAction(
  '[Driver] Get driver annual ride execution data fail',
  props<{ error: HttpException }>()
);

export const GetDriverTaxDocuments = createAction('[Driver] Get driver tax documents', props<{ taxYear: number }>());

export const GetDriverTaxDocumentsSuccess = createAction(
  '[Driver] Get driver tax documents success',
  props<{ taxDocuments: Array<TaxDocument> }>()
);

export const GetDriverTaxDocumentsFail = createAction('[Driver] Get driver tax documents fail', props<{ error: HttpException }>());

export const SelectTabIndex = createAction('[Driver] Select tab index', props<{ selectedTabIndex: number }>());
